import React, { useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaUser,
  FaHome,
  FaBuilding,
  FaUsers,
  FaVrCardboard,
  FaCreditCard,
  FaHeart,
  FaSearch,
  FaTag,
  FaSignOutAlt,
} from "react-icons/fa";
import { IoCloseCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";

import Profile from "./Profile";
import MyProperties from "./MyProperties";
import MyProjects from "./MyProjects";
import MyLeads from "./MyLeads";
import MyVirtualTour from "./MyVirtualTour";
import Subscription from "./Subscription";
import SavedSerches from "./SavedSerches";
import MyFavourite from "./MyFavourite";
import Offer from "./Offer";

const Dashboard = () => {
  const location = useLocation();
  const history = useHistory();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(
    location.state?.page || "profile"
  );
  const [showModal, setShowModal] = useState(false);

  const userType = sessionStorage.getItem("user_type");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location.state?.page) {
      setActiveSection(location.state.page);
    }
  }, [location.state]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setIsSidebarOpen(false);
  };

  const confirmLogout = () => {
    sessionStorage.clear();
    setShowModal(false);
    toast.success("Logout Successfully");
    history.push("/");
  };

  const navigationItems = [
    { label: "Profile", section: "profile", icon: <FaUser /> },
    { label: "My Properties", section: "myProperties", icon: <FaHome /> },
    ...(userType !== "Owner"
      ? [{ label: "My Projects", section: "myProjects", icon: <FaBuilding /> }]
      : []),
    { label: "My Leads", section: "myLeads", icon: <FaUsers /> },
    {
      label: "Virtual Tours",
      section: "myVirtualtour",
      icon: <FaVrCardboard />,
    },
    {
      label: "Subscriptions",
      section: "mySubscriptions",
      icon: <FaCreditCard />,
    },
    { label: "Favourites", section: "myFavourite", icon: <FaHeart /> },
    { label: "Saved Searches", section: "saveSerches", icon: <FaSearch /> },
    { label: "Offers", section: "offers", icon: <FaTag /> },
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case "myProperties":
        return <MyProperties />;
      case "myProjects":
        return <MyProjects />;
      case "myLeads":
        return <MyLeads />;
      case "myVirtualtour":
        return <MyVirtualTour />;
      case "mySubscriptions":
        return <Subscription />;
      case "saveSerches":
        return <SavedSerches />;
      case "myFavourite":
        return <MyFavourite />;
      case "offers":
        return <Offer />;
      case "profile":
      default:
        return <Profile />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 md:hidden">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-slate-400">
            Account
          </p>
          <h1 className="text-lg font-semibold text-slate-900">Dashboard</h1>
        </div>

        <button
          type="button"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          className="rounded-lg border border-slate-200 p-2.5 text-slate-700 hover:bg-slate-50"
        >
          {isSidebarOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>

      <div className="mx-auto flex min-h-screen max-w-[1600px]">
        {/* Mobile backdrop */}
        {isSidebarOpen && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 z-30 bg-slate-950/40 md:hidden"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-slate-950 px-5 py-6 text-white transition-transform duration-300 md:sticky md:top-0 md:h-screen md:translate-x-0 ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="mb-10 flex items-center justify-between px-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
                Account
              </p>
              <h2 className="mt-1 text-xl font-semibold">Dashboard</h2>
            </div>

            <button
              type="button"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Close menu"
              className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white md:hidden"
            >
              <FaTimes />
            </button>
          </div>

          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Menu
          </p>

          <nav className="flex-1 space-y-1">
            {navigationItems.map((item) => {
              const isActive = activeSection === item.section;

              return (
                <button
                  key={item.section}
                  type="button"
                  onClick={() => handleSectionChange(item.section)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm transition-colors ${
                    isActive
                      ? "bg-white text-slate-950 font-semibold shadow-sm"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="border-t border-white/10 pt-4">
            <Link
              to="/"
              className="mb-2 block rounded-xl px-3 py-2 text-sm text-slate-400 transition hover:bg-white/10 hover:text-white"
            >
              ← Back to home
            </Link>

            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-slate-300 transition hover:bg-red-500/10 hover:text-red-300"
            >
              <FaSignOutAlt />
              <span>Log out</span>
            </button>
          </div>
        </aside>

        {/* Main content */}
        <div className="min-w-0 flex-1">
          {/* Desktop top bar */}
          <header className="hidden items-center justify-between border-b border-slate-200 bg-white px-8 py-5 md:flex lg:px-10">
            <div>
              <p className="text-sm text-slate-500">
                <Link to="/" className="transition hover:text-slate-900">
                  Home
                </Link>
                <span className="mx-2 text-slate-300">/</span>
                <span className="font-medium text-slate-800">Dashboard</span>
              </p>
            </div>
            <span className="text-sm text-slate-500">Your account</span>
          </header>

          <main className="p-4 sm:p-6 md:p-8 lg:p-10">
            <div className="mx-auto max-w-6xl">
              <div className="mb-6">
                <p className="text-sm text-slate-500">Manage your account</p>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  {navigationItems.find((item) => item.section === activeSection)
                    ?.label || "Profile"}
                </h1>
              </div>

              <div className="min-h-[400px] rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                {renderActiveSection()}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Logout confirmation */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-title"
        >
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              aria-label="Close dialog"
              className="absolute right-4 top-4 text-2xl text-slate-400 transition hover:text-slate-700"
            >
              <IoCloseCircleOutline />
            </button>

            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
              <FaSignOutAlt />
            </div>

            <h2
              id="logout-title"
              className="text-xl font-semibold text-slate-900"
            >
              Log out?
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              You’ll need to sign in again to access your account.
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLogout}
                className="flex-1 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
